import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Link,
  useNavigate,
  useLocation,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { Stack } from "react-bootstrap";
import { GameCategory } from "@nestcoinco/hyper-api-gateway-api-client";
import { useTranslation } from "react-i18next";

import "./GenresPage.scss";
import { ROUTES, usePlayGameMetaDescription } from "../../utils/constants";
import GenreTag from "../../components/games-tab/GenreTag/GenreTag";
import LoveOutlineIcon from "../../components/icons/LoveOutlineIcon";
import ChevronLeftCircleIcon from "../../components/icons/ChevronLeftCircleIcon";
import DownloadAppPrompt, {
  PROMPT_DOWNLOAD_SCENARIOS,
} from "../../components/DownloadAppPrompt/DownloadAppPrompt";
import useDisclosure from "../../hooks/useDisclosure";
import Skeleton from "../../components/Skeleton/Skeleton";
import useIntersectionObserver from "../../hooks/useIntersectionObserver";
import useAuth from "../../hooks/useAuth";
import { ThemeVariant } from "../../utils/constants/generic-types";
import SkeletonList from "../../components/SkeletonList/SkeletonList";
import useGameCategoriesQuery from "../../react-query/queries/use-game-categories";
import useInfiniteGamesQuery from "../../react-query/queries/use-infinite-games";
import { CustomSpinner } from "../../components/CustomSpinner/CustomSpinner";
import useFavoriteGameMutation from "../../react-query/mutations/use-favorite-game";
import GameTile from "../../components/dashboard-page/GameTile/GameTile";
import {
  extractOgTitleFromMainTitle,
  renderHeaders,
} from "../../utils/renderHeaders";
import { capitalizeFirstLetter, getCurrentOrigin } from "../../utils/helpers";

interface GameCardProps {
  id?: string;
  name: string;
  slug?: string;
  image: string;
  playCount?: number;
  playIconColor?: string;
  userIsLoggedIn: boolean;
  isFavorite?: boolean;
  openDownloadAppPrompt: () => void;
  linkTo?: string;
}

const SKELETON_SHIMMER_THEME = ThemeVariant.DARK;

export function GameListSkeleton({
  gameListIsLoading,
}: {
  gameListIsLoading: boolean;
}) {
  if (!gameListIsLoading) return null;
  return (
    <SkeletonList noOfItems={8}>
      <Stack gap={1}>
        <Skeleton
          height={158}
          isLoading={gameListIsLoading}
          theme={SKELETON_SHIMMER_THEME}
        />
        <Skeleton
          height={20}
          borderRadius={16}
          isLoading={gameListIsLoading}
          theme={SKELETON_SHIMMER_THEME}
        />
        <Skeleton
          height={16}
          width={95}
          borderRadius={16}
          isLoading={gameListIsLoading}
          theme={SKELETON_SHIMMER_THEME}
        />
      </Stack>
    </SkeletonList>
  );
}

export function GameCard(props: GameCardProps) {
  const {
    id,
    name,
    slug,
    image,
    playCount,
    playIconColor,
    userIsLoggedIn,
    isFavorite,
    openDownloadAppPrompt,
    linkTo,
  } = props;
  const [gameIsFavorite, setGameIsFavorite] = useState<boolean | undefined>(
    isFavorite
  );
  const { addGameToFavorites, removeGameFromFavorites } =
    useFavoriteGameMutation();

  const handleFavouriteGame = () => {
    if (!userIsLoggedIn) {
      openDownloadAppPrompt();
      return;
    }

    if (gameIsFavorite) {
      setGameIsFavorite(false);
      removeGameFromFavorites(id ?? "");
    } else {
      setGameIsFavorite(true);
      addGameToFavorites(id ?? "");
    }
  };

  return (
    <div className="GameCard">
      <GameTile
        name={name}
        slug={slug}
        thumbnail={image}
        playCount={playCount}
        playIconColor={playIconColor ?? "#595959"}
        className="CustomGameTile"
        linkTo={linkTo}
      />

      <button
        className="rounded-circle d-inline-flex justify-content-center align-items-center LikeButton"
        onClick={handleFavouriteGame}
      >
        <LoveOutlineIcon
          width={19}
          height={19}
          stroke={gameIsFavorite ? "#FC4E5D" : "white"}
          fill={gameIsFavorite ? "#FC4E5D" : "transparent"}
        />
      </button>
    </div>
  );
}

export default function Genres() {
  const navigate = useNavigate();
  const loaderRef = useRef<HTMLDivElement>(null);
  const { genreName } = useParams();
  const [queryParams] = useSearchParams();
  const genreQuery = queryParams.get("g");
  const { pathname } = useLocation();
  const { isLoggedIn } = useAuth();
  const { data: gameCategories, isLoading: gameCategoriesAreLoading } =
    useGameCategoriesQuery();
  const activeGenreRef = useCallback((node: HTMLButtonElement) => {
    node?.focus();
  }, []);
  const [activeGenreId, setActiveGenreId] = useState<string | undefined>();
  const { allGames, query: allGamesQuery } = useInfiniteGamesQuery({
    category: activeGenreId,
  });
  const metaDescriptions = usePlayGameMetaDescription();
  const { t } = useTranslation();
  const {
    isOpen: isDownloadAppPromptOpen,
    onOpen: openDownloadAppPrompt,
    onClose: closeDownloadAppPrompt,
  } = useDisclosure();

  const pageTitles = useMemo(() => {
    const genre = genreName ? capitalizeFirstLetter(genreName) : "";
    const title = genre
      ? t("metadata:genre_games_play_the_best_genre_games_hyper_gaming_app", {
          genre,
        })
      : t("metadata:play_the_best_games_in_every_genre_hyper_gaming_app");
    const ogTitle = extractOgTitleFromMainTitle(title);
    return { title, ogTitle };
  }, [t, genreName]);

  const handleGenreSelection = (selectedGenre: GameCategory) => {
    const { id, name } = selectedGenre;
    if (id === activeGenreId) {
      setActiveGenreId(undefined);
    } else {
      setActiveGenreId(id);
    }

    if (
      !genreName ||
      (genreName?.toLowerCase() === name?.toLowerCase() && !genreQuery)
    ) {
      return;
    }
    if (
      genreName?.toLowerCase() === name?.toLowerCase() ||
      genreQuery?.toLowerCase() === name?.toLowerCase()
    ) {
      navigate(pathname);
      return;
    }
    navigate(`${pathname}?g=${name?.toLowerCase()}`);
  };

  useIntersectionObserver({
    targetRef: loaderRef,
    options: { threshold: 0.1 },
    callback: ([entry]) => {
      if (entry.isIntersecting && allGamesQuery.hasNextPage) {
        allGamesQuery.fetchNextPage();
      }
    },
  });

  useEffect(() => {
    if (!genreName) return;

    const matchedCategory = gameCategories?.find(
      (category) => category.name?.toLowerCase() === genreName.toLowerCase()
    );
    const genreId = matchedCategory?.id;
    setActiveGenreId(genreId);
  }, [gameCategories, genreName]);

  return (
    <>
      {renderHeaders({
        title: pageTitles.title,
        description: metaDescriptions.generatePlayGameDescription(
          undefined,
          genreName
        ),
        ogTitle: pageTitles.ogTitle,
        canonicalUrl: genreQuery
          ? `${getCurrentOrigin()}/games/genres/${genreQuery}`
          : `${getCurrentOrigin()}${pathname}`,
      })}

      <div className="MobileOnly GenresPage">
        <div className="PageHeader">
          <button
            className="p-0 bg-transparent"
            onClick={() => navigate(-1)}
            aria-label="Go back"
          >
            <ChevronLeftCircleIcon bgColor="#C7C7C780" />
          </button>{" "}
          <h1
            className={`Hanson-700 SpecialFontSettings PageTitle ${
              isLoggedIn ? "LoggedInUser" : ""
            }`}
          >
            Genres
          </h1>
          {!isLoggedIn && (
            <Link
              to={`${ROUTES.ROOT.ONBOARDING}/${ROUTES.ONBOARDING.WELCOME_BACK}`}
              className="text-uppercase text-decoration-none Grotesk LoginCta"
            >
              Login
            </Link>
          )}
        </div>

        <Stack className="mt-4 GamesLayout">
          <Stack
            direction="horizontal"
            className="justify-content-around scrollbar-hidden GenreList"
          >
            <SkeletonList
              noOfItems={5}
              skeletonProps={{
                isLoading: gameCategoriesAreLoading,
                height: 30,
                borderRadius: 16,
                theme: SKELETON_SHIMMER_THEME,
              }}
            />
            {gameCategories?.map((genre) => (
              <GenreTag
                key={genre.id}
                ref={activeGenreId === genre.id ? activeGenreRef : undefined}
                genre={genre.name ?? ""}
                icon={genre.icon ?? ""}
                fontSize="13px"
                containerClass={`GenreItem ${
                  activeGenreId === genre.id ? "IsSelected" : ""
                }`}
                handleClick={() => handleGenreSelection(genre)}
              />
            ))}
          </Stack>

          <div className="GamesSection">
            <div className="scrollbar-hidden GamesGrid">
              <GameListSkeleton
                gameListIsLoading={allGamesQuery.isLoading || !allGames.length}
              />
              {!!allGames.length &&
                allGames.map((game) => {
                  const {
                    id,
                    name,
                    slug,
                    thumbnail,
                    playCount,
                    isUserFavorite,
                  } = game;
                  return (
                    <GameCard
                      key={id}
                      id={id}
                      slug={slug}
                      name={name}
                      image={thumbnail ?? ""}
                      playCount={playCount ?? 0}
                      userIsLoggedIn={isLoggedIn}
                      isFavorite={isUserFavorite}
                      openDownloadAppPrompt={openDownloadAppPrompt}
                    />
                  );
                })}
            </div>
            <div ref={loaderRef} className="d-flex justify-content-center py-2">
              {allGamesQuery.isFetchingNextPage && (
                <CustomSpinner className="LoadingSpinner" />
              )}
            </div>
          </div>
        </Stack>
      </div>

      <DownloadAppPrompt
        isOpen={isDownloadAppPromptOpen}
        onOpen={openDownloadAppPrompt}
        onClose={closeDownloadAppPrompt}
        promptDownloadScenario={
          PROMPT_DOWNLOAD_SCENARIOS.LOGGED_OUT.CLICKS_FAVORITE_BUTTON
        }
      />
    </>
  );
}
